/**
 * FK-safe serial flush order (docs/08 §4).
 * Purchase confirm path: merchants → purchase_queries → recommendation_results →
 * transactions → transaction_splits → cash_reservations.
 * Remaining synced tables are ordered by typical dependency so same-timestamp
 * outbox batches still flush without FK violations.
 */

import type { OutboxEntry, SyncedTableName } from '../schemas/local-only';

export const FLUSH_TABLE_ORDER: readonly SyncedTableName[] = [
  'evidence_sources',
  'cards',
  'import_batches',
  'settings',
  'card_financial_observations',
  'card_autopay_observations',
  'grace_confirmations',
  'field_assertions',
  'reward_rules',
  'benefits',
  'card_fee_schedules',
  'merchants',
  'offers',
  'statement_cycles',
  'purchase_queries',
  'recommendation_results',
  'payment_history_coverage',
  'transactions',
  'transaction_splits',
  'card_payments',
  'payment_allocations',
  'cash_reservations',
  'deferred_interest_exposures',
  'recurring_charges',
  'merchant_category_observations',
  'review_items',
] as const;

const ORDER_INDEX: ReadonlyMap<SyncedTableName, number> = new Map(
  FLUSH_TABLE_ORDER.map((name, index) => [name, index]),
);

export function flushTableIndex(table: SyncedTableName): number {
  const index = ORDER_INDEX.get(table);
  if (index === undefined) {
    throw new Error(`flushOrder: unknown table ${table}`);
  }
  return index;
}

/** Sort key: FK table order, then createdAt, then id (stable, serial). */
export function compareOutboxFlushOrder(a: OutboxEntry, b: OutboxEntry): number {
  const byTable = flushTableIndex(a.table) - flushTableIndex(b.table);
  if (byTable !== 0) return byTable;
  if (a.createdAt < b.createdAt) return -1;
  if (a.createdAt > b.createdAt) return 1;
  if (a.id < b.id) return -1;
  if (a.id > b.id) return 1;
  return 0;
}
